import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Trash2, FolderOpen, FileVideo, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import useDebounce from '../../hooks/useDebounce';
import type { SharedWithMeItem } from '../../sdk/client/api/files/types';
import { apiV2 } from '../../lib/baseAxios';

interface TeamShare {
  _id: string;
  teamId: string;
  parentId: string | null;
  ownerId: string;
  ownerName?: string;
  name: string;
  thumbUrl?: string;
  createdAt: string;
  sharedAt?: string;
  source?: string;
}

interface SharedWithMeResponse {
  success: boolean;
  data: {
    teamShares: TeamShare[];
    publicShares: SharedWithMeItem[];
    hasMore: boolean;
    total: number;
  };
  message?: string;
}

interface SharedWithMeModalProps {
  open: boolean;
  onClose: () => void;
}

type SharedItem =
  | { type: 'team'; data: TeamShare }
  | { type: 'public'; data: SharedWithMeItem };

const PAGE_SIZE = 50;

const SharedWithMeModal: React.FC<SharedWithMeModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<SharedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [itemToRemove, setItemToRemove] = useState<SharedWithMeItem | null>(
    null,
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const loadSharedItems = useCallback(
    async (pageNum: number, append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);
        const response = await apiV2.get<SharedWithMeResponse>(
          '/shared-with-me',
          { params: { page: pageNum, limit: PAGE_SIZE } },
        );

        if (response.data.success && response.data.data) {
          const teamShares = response.data.data.teamShares || [];
          const publicShares = response.data.data.publicShares || [];
          const newHasMore = response.data.data.hasMore ?? false;

          // Items are already sorted by date from the backend
          const newItems: SharedItem[] = [
            ...teamShares.map(
              (item) => ({ type: 'team', data: item }) as SharedItem,
            ),
            ...publicShares.map(
              (item) => ({ type: 'public', data: item }) as SharedItem,
            ),
          ].sort((a, b) => {
            const dateA = new Date(a.data.createdAt).getTime();
            const dateB = new Date(b.data.createdAt).getTime();
            return dateB - dateA;
          });

          setHasMore(newHasMore);

          if (append) {
            setItems((prev) => [...prev, ...newItems]);
          } else {
            setItems(newItems);
          }
        } else {
          setError('Failed to load shared items');
        }
      } catch (err) {
        console.error('[SharedWithMeModal] Error loading shared items:', err);
        setError('Failed to load shared items');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  // Initial fetch when modal opens
  useEffect(() => {
    if (!open) return;

    setItems([]);
    setSearchQuery('');
    setPage(1);
    setHasMore(false);
    loadSharedItems(1);
  }, [open, loadSharedItems]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when user scrolls within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadSharedItems(nextPage, true);
    }
  }, [loadingMore, hasMore, page, loadSharedItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Filter items based on search query (client-side filter on loaded items)
  const filteredItems = React.useMemo(() => {
    if (!debouncedSearchQuery) return items;

    const query = debouncedSearchQuery.toLowerCase();
    return items.filter((item) => {
      const name =
        item.type === 'team' ? item.data.name : item.data.resource.name;
      return name.toLowerCase().includes(query);
    });
  }, [items, debouncedSearchQuery]);

  const handleNavigateToTeamShare = (file: TeamShare) => {
    const { teamId, parentId, ownerId, _id, source } = file;

    let url: string;
    if (source === 'clone') {
      url = `/library/${teamId}/recents/${_id}`;
    } else if (parentId) {
      url = `/shared/${teamId}/f/${parentId}/${_id}`;
    } else {
      url = `/shared/${teamId}/u/${ownerId}/${_id}`;
    }

    onClose();
    navigate(url);
  };

  const handlePublicShareClick = (item: SharedWithMeItem) => {
    const baseUrl = window.location.origin;
    const prefix = item.resource.type === 'Folder' ? 'f' : 'v';
    const sharedUrl = `${baseUrl}/${prefix}/${item.shareId}`;
    onClose();
    window.location.href = sharedUrl;
  };

  const handleRemoveClick = (item: SharedWithMeItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToRemove(item);
  };

  const confirmRemove = async () => {
    if (!itemToRemove) return;

    try {
      const response = await apiV2.delete(
        `/shared-with-me/${itemToRemove._id}`,
      );

      if (response.data.success) {
        setItems((prev) =>
          prev.filter(
            (item) =>
              !(item.type === 'public' && item.data._id === itemToRemove._id),
          ),
        );
      }
    } catch (err) {
      console.error('Error removing shared item:', err);
    } finally {
      setItemToRemove(null);
    }
  };

  const handleItemClick = (item: SharedItem) => {
    if (item.type === 'team') {
      handleNavigateToTeamShare(item.data);
    } else {
      handlePublicShareClick(item.data);
    }
  };

  const renderThumbnail = (item: SharedItem) => {
    if (item.type === 'team') {
      if (item.data.thumbUrl) {
        return (
          <img
            src={item.data.thumbUrl}
            alt={item.data.name}
            className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-gray-800"
          />
        );
      }
      return (
        <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
          <FileVideo className="w-4 h-4 text-gray-400 dark:text-gray-600 opacity-50" />
        </div>
      );
    }

    // Public share
    const { resource } = item.data;
    if (resource.thumbUrl) {
      return (
        <img
          src={resource.thumbUrl}
          alt={resource.name}
          className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-gray-800"
        />
      );
    }

    const Icon = resource.type === 'Folder' ? FolderOpen : FileVideo;
    return (
      <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
        <Icon className="w-4 h-4 text-gray-400 dark:text-gray-600 opacity-50" />
      </div>
    );
  };

  const getItemName = (item: SharedItem): string => {
    return item.type === 'team' ? item.data.name : item.data.resource.name;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="h-[480px] max-h-[80vh] overflow-hidden focus:outline-none focus:ring-0 p-0 gap-0 flex flex-col sm:max-w-[600px]">
          {/* Search Header */}
          <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t(
                'shared.search.placeholder',
                'Search shared items...',
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-base"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </div>

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">
            <div
              ref={scrollContainerRef}
              className="overflow-y-auto overflow-x-hidden w-full"
            >
              {(() => {
                if (loading) {
                  return (
                    <div className="pt-3">
                      <div className="space-y-2">
                        {Array.from({ length: 8 }, (_, i) => (
                          <div key={i} className="flex gap-3 px-6 py-2.5">
                            <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                            <div className="flex-1 space-y-1">
                              <Skeleton className="h-4 w-full max-w-[70%]" />
                              <Skeleton className="h-3 w-full max-w-[40%]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                if (error) {
                  return (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center space-y-1">
                        <p className="text-sm font-medium text-destructive">
                          {error}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            'shared.error.retry',
                            'Please try refreshing the page',
                          )}
                        </p>
                      </div>
                    </div>
                  );
                }

                if (filteredItems.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="rounded-full bg-muted p-4 mb-3">
                        <FolderOpen className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-gray-500">
                        {searchQuery
                          ? t(
                              'shared.search.noResults',
                              'No shared items found',
                            )
                          : t('shared.empty.title', 'No shared items yet')}
                      </p>
                      {!searchQuery && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {t(
                            'shared.empty.description',
                            'Items shared with you by others will appear here',
                          )}
                        </p>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="pt-3">
                    <div>
                      {filteredItems.map((item) => {
                        const itemId =
                          item.type === 'team'
                            ? `team-${item.data._id}`
                            : `public-${item.data._id}`;
                        const itemName = getItemName(item);

                        return (
                          <div
                            key={itemId}
                            className="group relative w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
                            onClick={() => handleItemClick(item)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleItemClick(item);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            {/* Thumbnail */}
                            {renderThumbnail(item)}

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm truncate">
                                {itemName}
                              </span>
                              <p className="text-xs text-muted-foreground">
                                {new Date(
                                  item.type === 'team'
                                    ? item.data.sharedAt || item.data.createdAt
                                    : item.data.createdAt,
                                ).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                                {item.type === 'team' &&
                                  item.data.ownerName && (
                                    <>
                                      <span className="mx-1.5">|</span>
                                      {item.data.ownerName}
                                    </>
                                  )}
                              </p>
                            </div>

                            {/* Remove button for public shares only */}
                            {item.type === 'public' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 hover:bg-destructive hover:text-destructive-foreground"
                                onClick={(e) => handleRemoveClick(item.data, e)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}

                      {/* Loading more indicator */}
                      {loadingMore && (
                        <div className="flex justify-center py-3">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!itemToRemove}
        onOpenChange={(dialogOpen) => !dialogOpen && setItemToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('shared.removeDialog.title', 'Remove from shared with me?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'shared.removeDialog.description',
                'This will remove "{{name}}" from your shared list. You can access it again if someone shares it with you.',
                { name: itemToRemove?.resource.name },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('common.remove', 'Remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SharedWithMeModal;
